import React, { useState } from 'react';
import { useAuthStore } from '../stores/authStore';

export function AuthScreen() {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const isLoading = useAuthStore((s) => s.isLoading);
  const error = useAuthStore((s) => s.error);
  const login = useAuthStore((s) => s.login);
  const register = useAuthStore((s) => s.register);
  const clearError = useAuthStore((s) => s.clearError);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  const switchMode = (newMode: 'login' | 'register') => {
    setEmail('');
    setPassword('');
    setFirstName('');
    setLastName('');
    setMode(newMode);
    clearError();
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    await login(email, password);
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    await register(email, password, firstName || undefined, lastName || undefined);
  };

  return (
    <div className="fixed top-0 left-0 w-full h-full bg-neutral-600 flex flex-col items-center justify-center text-neutral-200 font-primary z-[10000]">
      {/* Logo */}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 1080 896.55"
        className="w-[100px] h-[100px] mb-lg"
      >
        <defs>
          <style>
            {`
              .cls-1 { fill: #ce0d0d; }
              .cls-2 { fill: #e9222c; }
              .cls-3 { fill: #ed2827; }
            `}
          </style>
        </defs>
        <polygon className="cls-3" points="212.58 341.99 0 554.57 313.26 554.57 212.58 341.99" />
        <polygon className="cls-1" points="525.84 341.99 212.58 341.99 313.26 554.57 525.84 341.99" />
        <polygon className="cls-3" points="0 896.55 313.26 896.55 212.58 683.97 0 896.55" />
        <polygon className="cls-1" points="313.26 896.55 525.84 683.97 212.58 683.97 313.26 896.55" />
        <polygon className="cls-3" points="908.27 681.7 506.05 554.67 779.29 896.55 1080 896.55 908.27 681.7" />
        <path className="cls-3" d="M313.26,212.58L525.84,0h269.09c41.12,0,79.75,10.6,113.34,29.19,25.81,14.28,48.65,33.27,67.34,55.82,29.28,35.35,48.41,79.27,52.82,127.6.75,7.24,1.08,14.56,1.08,21.96,0,64.8-26.29,123.36-68.71,165.87l-52.16,52.16-101.96,101.98.07.08-300.52-.05-.19.05,304.45-304.54c3.91-3.99,6.41-9.48,6.41-15.55,0-12.15-9.82-21.96-21.96-21.96l-481.67-.04Z" />
        <polygon className="cls-1" points="806.74 554.67 506.05 554.67 908.27 681.7 806.74 554.67" />
        <polygon className="cls-2" points="212.56 0 96.27 116.3 .02 212.59 313.24 212.59 212.56 0" />
        <polygon className="cls-1" points="525.84 0 212.58 0 313.26 212.59 525.84 0" />
      </svg>

      {/* Card */}
      <div className="bg-neutral-700 rounded-medium p-lg border border-neutral-400 w-full max-w-sm mx-4">
        <h2 className="text-lg font-semibold text-neutral-100 mb-md text-center">
          {mode === 'login' ? 'Sign In' : 'Create Account'}
        </h2>

        {error && (
          <div className="bg-red-800/30 border border-red-500/40 rounded-small px-sm py-xs text-sm text-red-200 mb-md">
            {error}
          </div>
        )}

        {mode === 'login' ? (
          <form key="login" onSubmit={handleLogin} className="flex flex-col gap-sm" autoComplete="on">
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="settings-input w-full"
              autoComplete="email"
              required
            />
            <input
              type="password"
              name="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="settings-input w-full"
              autoComplete="current-password"
              required
            />
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full flex items-center justify-center gap-xs mt-xs"
            >
              {isLoading && (
                <div className="w-4 h-4 border-2 border-neutral-white/30 border-t-neutral-white rounded-full animate-spin" />
              )}
              Sign In
            </button>
            <p className="text-xs text-neutral-300 text-center mt-xs">
              Don&apos;t have an account?{' '}
              <button
                type="button"
                onClick={() => switchMode('register')}
                className="text-red-400 hover:text-red-300 underline cursor-pointer bg-transparent border-0 p-0 text-xs"
              >
                Register
              </button>
            </p>
          </form>
        ) : (
          <form key="register" onSubmit={handleRegister} className="flex flex-col gap-sm" autoComplete="off">
            <div className="flex gap-sm">
              <input
                type="text"
                name="reg-firstName"
                placeholder="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="settings-input w-full min-w-0"
                autoComplete="off"
              />
              <input
                type="text"
                name="reg-lastName"
                placeholder="Last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="settings-input w-full min-w-0"
                autoComplete="off"
              />
            </div>
            <input
              type="email"
              name="reg-email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="settings-input w-full"
              autoComplete="off"
              required
            />
            <input
              type="password"
              name="reg-password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="settings-input w-full"
              autoComplete="new-password"
              required
            />
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full flex items-center justify-center gap-xs mt-xs"
            >
              {isLoading && (
                <div className="w-4 h-4 border-2 border-neutral-white/30 border-t-neutral-white rounded-full animate-spin" />
              )}
              Create Account
            </button>
            <p className="text-xs text-neutral-300 text-center mt-xs">
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => switchMode('login')}
                className="text-red-400 hover:text-red-300 underline cursor-pointer bg-transparent border-0 p-0 text-xs"
              >
                Sign In
              </button>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
